import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Chip,
  Avatar,
  LinearProgress,
  Stack,
  Divider,
  Button,
  Tooltip,
  useTheme,
  Theme,
  List,
  ListItem,
  Tabs,
  Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DateTime } from "luxon";

// Icons
import MenuBookIcon from "@mui/icons-material/MenuBook";
import HeadsetIcon from "@mui/icons-material/Headset";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import CreateIcon from "@mui/icons-material/Create";
import GavelIcon from "@mui/icons-material/Gavel";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TranslateIcon from "@mui/icons-material/Translate";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchoolIcon from "@mui/icons-material/School";
import QuizIcon from "@mui/icons-material/Quiz";

import { useNavigate } from "react-router-dom";
import { getCourseProgress } from "../../services/studentService";
import {
  Module,
  ProgramWeek,
  ProgramWeekStatus,
} from "../../types/ProgramModel";
import { useAuth } from "../../context/AuthContext";
import { useStudentClassroom } from "../../context/StudentClassroomContext";
import { learningStorageService } from "../../services/learningStorageService";
import QuizAttemptsModal from "../../components/QuizAttemptsModal";

interface CourseProgress {
  currentWeek: number;
  overallProgress: number;
  lastActivity: string;
  weeklyContent: ProgramWeek[];
}

// Local ViewModel for week data
type WeekVM = {
  weekName: string;
  status: ProgramWeekStatus;
  progress: number;
  startLabel?: string;
  endLabel?: string;
  weekNumber: number;
  weekId?: string;
  modules: Module[];
  unlockLabel?: string;
  startAt?: string;
  unlockAt?: string;
  isFutureWeek?: boolean;
};

// ============================================================
// Module Status Types
// ============================================================
type ModuleStatus =
  | "completed" // Both learning + quiz done
  | "quiz-available" // Learning done, quiz pending
  | "in-progress" // Has localStorage progress
  | "not-started"; // No progress

// Styled Components following HTML concept design
const SummarySection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
}));

const KPISection = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "2fr 1fr",
  gap: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
}));

const OverallProgressCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const CurrentWeekCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const WeekTimeline = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  paddingBottom: "0",
}));

const TimelineHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
}));

const TimelineList = styled(List)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "24px",
  overflowX: "auto",
  padding: 0,
  paddingBottom: "1rem",
  minWidth: "max(100%, 720px)",

  WebkitOverflowScrolling: "touch",
  cursor: "grab",
  "&:active": {
    cursor: "grabbing",
  },

  "&::-webkit-scrollbar": {
    height: 4,
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.divider,
    borderRadius: 2,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: theme.palette.action.hover,
  },
  scrollbarWidth: "thin",
}));

const TimelineStep = styled(ListItem)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: 0,
  minWidth: "80px",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 22,
    left: "100%",
    width: "24px",
    height: "2px",
    backgroundColor: theme.palette.divider,
    zIndex: 0,
  },
  "&:last-child::after": {
    display: "none",
  },
}));

const TimelineButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== "weekStatus",
})<{ weekStatus: ProgramWeekStatus }>(({ theme, weekStatus }) => ({
  position: "relative",
  zIndex: 1,
  width: 44,
  height: 44,
  border: `2px solid ${
    weekStatus === ProgramWeekStatus.Completed
      ? theme.palette.success.main
      : weekStatus === ProgramWeekStatus.InProgress
      ? theme.palette.primary.main
      : weekStatus === ProgramWeekStatus.Locked
      ? theme.palette.action.disabled
      : theme.palette.warning.main
  }`,
  backgroundColor:
    weekStatus === ProgramWeekStatus.InProgress
      ? theme.palette.primary.main
      : theme.palette.background.paper,
  color:
    weekStatus === ProgramWeekStatus.Completed
      ? theme.palette.success.main
      : weekStatus === ProgramWeekStatus.InProgress
      ? theme.palette.primary.contrastText
      : weekStatus === ProgramWeekStatus.Locked
      ? theme.palette.action.disabled
      : theme.palette.warning.main,
  fontWeight: 600,
  "&:hover": {
    backgroundColor:
      weekStatus === ProgramWeekStatus.InProgress
        ? theme.palette.primary.dark
        : weekStatus === ProgramWeekStatus.Locked
        ? theme.palette.background.paper
        : theme.palette.action.hover,
  },
  "&.Mui-disabled": {
    border: `2px solid ${theme.palette.action.disabled}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.action.disabled,
  },
}));

const WeekDetailCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(0.5),
}));

const ModuleTileGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
}));

// ModuleTile supports "quiz-available" status
const ModuleTile = styled(Card, {
  shouldForwardProp: (prop) => prop !== "moduleStatus",
})<{
  moduleStatus: ModuleStatus;
}>(({ theme, moduleStatus }) => ({
  cursor: "pointer",
  transition: "all 0.3s ease",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.spacing(1.5),
  "&:hover": {
    boxShadow: theme.shadows[4],
    transform: "translateY(-2px)",
  },
  ...(moduleStatus === "completed" && {
    background: `linear-gradient(135deg, ${theme.palette.success.light}15, ${theme.palette.success.light}05)`,
    borderColor: theme.palette.success.light,
  }),
  ...(moduleStatus === "quiz-available" && {
    background: `linear-gradient(135deg, ${theme.palette.info.light}15, ${theme.palette.info.light}05)`,
    borderColor: theme.palette.info.light,
  }),
  ...(moduleStatus === "in-progress" && {
    borderColor: theme.palette.primary.light,
  }),
}));

const ModuleHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1),
}));

// ModuleIcon supports "quiz-available" status
const ModuleIcon = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "moduleStatus",
})<{
  moduleStatus: ModuleStatus;
}>(({ theme, moduleStatus }) => ({
  width: 48,
  height: 48,
  backgroundColor:
    moduleStatus === "completed"
      ? theme.palette.success.light
      : moduleStatus === "quiz-available"
      ? theme.palette.info.light
      : moduleStatus === "in-progress"
      ? theme.palette.primary.light
      : theme.palette.grey[200],
  color:
    moduleStatus === "completed"
      ? theme.palette.success.dark
      : moduleStatus === "quiz-available"
      ? theme.palette.info.dark
      : moduleStatus === "in-progress"
      ? theme.palette.primary.main
      : theme.palette.text.secondary,
  fontSize: "20px",
}));

const ModuleActions = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  marginTop: "auto",
  flexWrap: "wrap",
}));

const GuidanceBox = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.info.light}15, ${theme.palette.info.light}05)`,
  border: `1px dashed ${theme.palette.info.main}`,
  borderRadius: theme.spacing(0.5),
  padding: theme.spacing(3),
  marginTop: theme.spacing(3),
}));

const MyClassroomPage: React.FC = () => {
  const DEFAULT_TIMEZONE = "Australia/Sydney";

  const theme = useTheme<Theme>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if all weeks should be unlocked (pace=0 means testing mode)
  const unlockAllWeeks = user?.myClassroom?.pace === 0;

  const {
    setCurrentProgram,
    setCurrentWeek,
    setCurrentModule,
    updateModuleProgress,
    moduleProgresses,
    currentProgram,
  } = useStudentClassroom();

  const [focusedWeek, setFocusedWeek] = useState<number>(1);
  const [weeklyContent, setWeeklyContent] = useState<WeekVM[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalWeeks, setTotalWeeks] = useState<number>(0);
  const [currentWeek, setCurrentWeekState] = useState<number>(1);
  const [overallProgress, setOverallProgress] = useState<number>(0);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const currentWeekRef = useRef<HTMLLIElement>(null);

  const [attemptsModalOpen, setAttemptsModalOpen] = useState(false);
  const [selectedModuleForAttempts, setSelectedModuleForAttempts] = useState<{
    moduleId: string;
    moduleName: string;
  } | null>(null);

  // Helper functions
  const formatDateForDisplay = (iso: string): string =>
    DateTime.fromISO(iso, { zone: DEFAULT_TIMEZONE }).toFormat("dd MMM yyyy");

  const formatDateRange = (startDate?: string, endDate?: string): string => {
    if (!startDate) return "";
    const start = DateTime.fromISO(startDate, {
      zone: DEFAULT_TIMEZONE,
    }).toFormat("dd MMM yyyy");
    const end = endDate
      ? DateTime.fromISO(endDate, { zone: DEFAULT_TIMEZONE }).toFormat(
          "dd MMM yyyy"
        )
      : "Ongoing";
    return `${start} — ${end}`;
  };

  const calculateUnlockDate = (
    startDate: string,
    weekNumber: number,
    pace?: number
  ): string => {
    const start = DateTime.fromISO(startDate, { zone: DEFAULT_TIMEZONE });
    const daysToAdd = (weekNumber - 1) * (pace && pace > 0 ? pace : 7);
    return start.plus({ days: daysToAdd }).toISO() || "";
  };

  const calculateTotalWeeks = (
    startDate?: string,
    endDate?: string,
    pace?: number
  ): number => {
    // When pace=0 (testing mode), return content length or program max
    if (!pace || pace <= 0) {
      return weeklyContent.length > 0 ? weeklyContent.length : 20;
    }

    if (!startDate || !endDate) {
      return Math.max(weeklyContent.length, 1);
    }

    const start = DateTime.fromISO(startDate, { zone: DEFAULT_TIMEZONE });
    const end = DateTime.fromISO(endDate, { zone: DEFAULT_TIMEZONE });
    const daysDiff = Math.max(0, Math.ceil(end.diff(start, "days").days));
    return Math.max(1, Math.ceil(daysDiff / pace) + 1);
  };

  const getUnlockCountdown = (iso: string): string => {
    const unlockDate = DateTime.fromISO(iso, { zone: DEFAULT_TIMEZONE });
    const now = DateTime.now().setZone(DEFAULT_TIMEZONE);
    const diff = unlockDate.diff(now, ["days", "hours"]).toObject();
    const days = Math.floor(diff.days || 0);
    const hours = Math.floor(diff.hours || 0);
    if (days > 0) return `Unlocks in ${days} day${days !== 1 ? "s" : ""}`;
    if (hours > 0) return `Unlocks in ${hours} hour${hours !== 1 ? "s" : ""}`;
    return "Unlocks soon";
  };

  const normaliseStatus = (
    s: string | ProgramWeekStatus | undefined
  ): ProgramWeekStatus => {
    if (!s) return ProgramWeekStatus.Incomplete;
    const val = typeof s === "string" ? s.toUpperCase() : s;
    switch (val) {
      case "IN_PROGRESS":
      case ProgramWeekStatus.InProgress:
        return ProgramWeekStatus.InProgress;
      case "COMPLETED":
      case ProgramWeekStatus.Completed:
        return ProgramWeekStatus.Completed;
      case "LOCKED":
      case ProgramWeekStatus.Locked:
        return ProgramWeekStatus.Locked;
      case "INCOMPLETE":
      case ProgramWeekStatus.Incomplete:
      default:
        return ProgramWeekStatus.Incomplete;
    }
  };

  const moduleIcons: Record<string, React.ReactNode> = {
    Reading: <MenuBookIcon />,
    Listening: <HeadsetIcon />,
    Speaking: <RecordVoiceOverIcon />,
    Writing: <CreateIcon />,
    Grammar: <GavelIcon />,
    Review: <AssignmentTurnedInIcon />,
    Vocabulary: <TranslateIcon />,
  };

  const statusChipColorMap: Record<
    ProgramWeekStatus,
    "primary" | "success" | "warning" | "default"
  > = {
    [ProgramWeekStatus.InProgress]: "primary",
    [ProgramWeekStatus.Completed]: "success",
    [ProgramWeekStatus.Incomplete]: "warning",
    [ProgramWeekStatus.Locked]: "default",
  };

  // ============================================================
  // FIXED: Module Status Functions - Check context FIRST
  // ============================================================

  const getModuleStatus = (module: Module): ModuleStatus => {
    // ✅ FIXED: Check in-memory context FIRST (most recent state)
    const moduleProgress = moduleProgresses[module.moduleId];
    const contextQuizDone = moduleProgress?.quizCompleted === true;

    // If context says quiz is done, module is completed
    if (contextQuizDone) {
      return "completed";
    }

    // Then check backend completion status
    const backendLearningDone =
      (module as any).learningContentCompleted === true;
    const backendQuizDone = (module as any).quizCompleted === true;
    const backendFullyComplete = (module as any).completed === true;

    // Fully completed from backend
    if (backendFullyComplete || (backendLearningDone && backendQuizDone)) {
      return "completed";
    }

    // Learning done, quiz pending (from backend)
    if (backendLearningDone && !backendQuizDone) {
      return "quiz-available";
    }

    // Check localStorage for in-progress learning content
    const hasLocalProgress = learningStorageService.hasLearningProgress(
      module.moduleId
    );
    if (hasLocalProgress) {
      return "in-progress";
    }

    // Check context for any progress
    if (moduleProgress && moduleProgress.completionPercentage > 0) {
      return "in-progress";
    }

    return "not-started";
  };

  const getModuleProgressPercent = (module: Module): number => {
    // Try localStorage first
    const localProgress = learningStorageService.getProgressPercentage(
      module.moduleId
    );
    if (localProgress > 0) {
      return localProgress;
    }

    // Fallback to context
    const moduleProgress = moduleProgresses[module.moduleId];
    return moduleProgress?.completionPercentage || 0;
  };

  const getStatusChipProps = (status: ModuleStatus, module: Module) => {
    switch (status) {
      case "completed":
        return {
          label: "Completed ✓",
          color: "success" as const,
          variant: "filled" as const,
        };
      case "quiz-available":
        return {
          label: "Quiz Available",
          color: "info" as const,
          variant: "filled" as const,
        };
      case "in-progress":
        const percent = getModuleProgressPercent(module);
        return {
          label: `In Progress ${percent > 0 ? `(${percent}%)` : ""}`,
          color: "primary" as const,
          variant: "outlined" as const,
        };
      case "not-started":
      default:
        return {
          label: "Not Started",
          color: "default" as const,
          variant: "outlined" as const,
        };
    }
  };

  const getModuleButtonProps = (status: ModuleStatus) => {
    switch (status) {
      case "completed":
        return {
          label: "Review",
          variant: "outlined" as const,
          icon: <VisibilityIcon />,
          color: "primary" as const,
        };
      case "quiz-available":
        return {
          label: "Take Quiz",
          variant: "contained" as const,
          icon: <QuizIcon />,
          color: "info" as const,
        };
      case "in-progress":
        return {
          label: "Continue",
          variant: "contained" as const,
          icon: <PlayArrowIcon />,
          color: "primary" as const,
        };
      case "not-started":
      default:
        return {
          label: "Start",
          variant: "contained" as const,
          icon: <PlayArrowIcon />,
          color: "primary" as const,
        };
    }
  };

  const getModuleSecondaryInfo = (
    status: ModuleStatus,
    module: Module
  ): string => {
    const moduleProgress = moduleProgresses[module.moduleId];

    switch (status) {
      case "completed":
        const score = moduleProgress?.quizScore || (module as any).quizScore;
        return score ? `Quiz Score: ${Math.round(score)}%` : "Module completed";

      case "quiz-available":
        return "Learning complete — Ready for quiz";

      case "in-progress":
        const percent = getModuleProgressPercent(module);
        return `${percent}% complete`;

      case "not-started":
      default:
        return `${module.questionCount || 0} questions`;
    }
  };

  // ============================================================
  // Calculate week progress based on module statuses (local calculation)
  // ============================================================
  const calculateWeekProgress = (modules: Module[]): number => {
    if (!modules || modules.length === 0) return 0;

    const completedCount = modules.filter(
      (m) => getModuleStatus(m) === "completed"
    ).length;

    return Math.round((completedCount / modules.length) * 100);
  };

  // Data transformation - only transforms API data
  const transformWeeklyContent = (cp: CourseProgress): WeekVM[] => {
    const { weeklyContent } = cp;
    if (!weeklyContent || weeklyContent.length === 0) return [];

    return weeklyContent.map((w) => {
      const status = normaliseStatus((w as any).status);
      const startAt = w.startDate || undefined;
      const unlockAt = w.unlockDate || undefined;

      const startLabel = startAt ? formatDateForDisplay(startAt) : "";
      const unlockLabel = unlockAt ? formatDateForDisplay(unlockAt) : "";
      const endLabel = "";

      // Use backend progress if available, otherwise 0
      const progress =
        typeof (w as any).progress === "number"
          ? (w as any).progress
          : Number((w as any).progress ?? 0);

      return {
        weekName: w.name || `Week ${w.weekNumber}`,
        status,
        progress,
        startLabel,
        endLabel,
        weekNumber: w.weekNumber,
        weekId: w.weekId,
        modules: w.modules || [],
        unlockLabel,
        startAt,
        unlockAt,
      };
    });
  };

  // Generate all weeks for timeline display
  const allWeeksForTimeline = useMemo((): WeekVM[] => {
    const classroom = user?.myClassroom;
    const plannedTotal = calculateTotalWeeks(
      classroom?.startDate,
      classroom?.endDate,
      classroom?.pace
    );

    const total = totalWeeks > 0 ? totalWeeks : plannedTotal;

    const existingWeeksMap = new Map<number, WeekVM>();
    weeklyContent.forEach((week) => {
      existingWeeksMap.set(week.weekNumber, week);
    });

    const allWeeks: WeekVM[] = [];
    for (let weekNum = 1; weekNum <= total; weekNum++) {
      const existingWeek = existingWeeksMap.get(weekNum);

      if (existingWeek) {
        allWeeks.push(existingWeek);
      } else {
        const unlockAt = classroom?.startDate
          ? calculateUnlockDate(classroom.startDate, weekNum, classroom.pace)
          : undefined;

        allWeeks.push({
          weekName: `Week ${weekNum}`,
          status: ProgramWeekStatus.Locked,
          progress: 0,
          startLabel: "",
          endLabel: "",
          weekNumber: weekNum,
          weekId: `future_week_${weekNum}`,
          modules: [],
          unlockLabel: unlockAt ? formatDateForDisplay(unlockAt) : "",
          unlockAt,
          isFutureWeek: true,
        });
      }
    }

    return allWeeks;
  }, [weeklyContent, user?.myClassroom, totalWeeks]);

  // ============================================================
  // Calculate overall progress based on all module statuses
  // ============================================================
  const calculatedOverallProgress = useMemo(() => {
    const allModules = weeklyContent.flatMap((w) => w.modules || []);
    if (allModules.length === 0) return 0;

    const completedCount = allModules.filter(
      (m) => getModuleStatus(m) === "completed"
    ).length;

    return Math.round((completedCount / allModules.length) * 100);
  }, [weeklyContent, moduleProgresses]);

  // Event handlers
  const handleModuleClick = (
    module: Module,
    weekNumber: number,
    startQuiz: boolean = false
  ) => {
    if (!module?.moduleId) return;

    setCurrentModule(module.moduleId);
    setCurrentWeek(weekNumber);

    const existing = moduleProgresses[module.moduleId];
    if (!existing) {
      const label = (module as any).name || (module as any).type || "Module";
      updateModuleProgress(module.moduleId, {
        moduleId: module.moduleId,
        moduleName: label,
        weekNumber,
        completionPercentage: 0,
        sectionsCompleted: [],
        quizCompleted: false,
        lastAccessed: Date.now(),
      });
    } else {
      updateModuleProgress(module.moduleId, { lastAccessed: Date.now() });
    }

    // Get module status to determine if review mode
    const moduleStatus = getModuleStatus(module);
    const learningDone = (module as any).learningContentCompleted === true;

    const queryParam = startQuiz ? "?startQuiz=true" : "";
    const moduleType = ((module as any).type || (module as any).name || "").toLowerCase();
    navigate(`/student/my-classroom/module/${module.moduleId}${queryParam}`, {
      state: {
        learningContentCompleted: learningDone,
        isReviewMode:
          moduleStatus === "completed" || moduleStatus === "quiz-available",
        moduleType,
      },
    });
  };

  const handleContinueLearning = () => {
    const currentWeekData = weeklyContent.find(
      (w) => w.weekNumber === currentWeek
    );

    if (!currentWeekData?.modules || currentWeekData.modules.length === 0) {
      return;
    }

    // Priority 1: Find first module with quiz available (learning done, quiz pending)
    const quizAvailable = currentWeekData.modules.find(
      (m) => getModuleStatus(m) === "quiz-available"
    );
    if (quizAvailable) {
      handleModuleClick(quizAvailable, currentWeek, true); // Start quiz directly
      return;
    }

    // Priority 2: Find first in-progress module (has localStorage progress)
    const inProgress = currentWeekData.modules.find(
      (m) => getModuleStatus(m) === "in-progress"
    );
    if (inProgress) {
      handleModuleClick(inProgress, currentWeek);
      return;
    }

    // Priority 3: Find first not-started module
    const notStarted = currentWeekData.modules.find(
      (m) => getModuleStatus(m) === "not-started"
    );
    if (notStarted) {
      handleModuleClick(notStarted, currentWeek);
      return;
    }

    // Priority 4: All complete - go to first module for review
    handleModuleClick(currentWeekData.modules[0], currentWeek);
  };
  const handleWeekSelect = (weekNumber: number) => {
    if (unlockAllWeeks || weekNumber <= currentWeek) {
      setFocusedWeek(weekNumber);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Data fetching
  useEffect(() => {
    const fetchWeeklyContent = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getCourseProgress();
        const courseProgress: CourseProgress = response.data;

        if (courseProgress?.weeklyContent) {
          const transformed = transformWeeklyContent(courseProgress);
          setWeeklyContent(transformed);
          setCurrentWeekState(courseProgress.currentWeek || 1);
          setOverallProgress(courseProgress.overallProgress || 0);
          setFocusedWeek(courseProgress.currentWeek || 1);

          if (user?.myClassroom?.programName) {
            setCurrentProgram(user.myClassroom.programName);
          }

          setCurrentWeek(courseProgress.currentWeek || 1);

          const plannedWeeks = calculateTotalWeeks(
            user?.myClassroom?.startDate,
            user?.myClassroom?.endDate,
            user?.myClassroom?.pace
          );
          setTotalWeeks(
            user?.myClassroom?.pace === 0
              ? transformed.length
              : plannedWeeks || transformed.length
          );
        } else {
          setWeeklyContent([]);
          setTotalWeeks(0);
        }
      } catch (err) {
        console.error("Error fetching weekly content:", err);
        setError("Failed to load content. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchWeeklyContent();
  }, [
    user?.myClassroom?.program,
    user?.myClassroom?.startDate,
    user?.myClassroom?.endDate,
    user?.myClassroom?.pace,
    setCurrentProgram,
    setCurrentWeek,
  ]);

  useEffect(() => {
    if (!loading && currentWeekRef.current) {
      setTimeout(() => {
        currentWeekRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }, 300);
    }
  }, [loading]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Loading your classroom…
          </Typography>
          <LinearProgress sx={{ maxWidth: 400, mx: "auto" }} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            border: "1px solid",
            borderColor: "error.main",
          }}
        >
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            We couldn't load your course
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Paper>
      </Container>
    );
  }

  const now = DateTime.now().setZone(DEFAULT_TIMEZONE);
  const currentDate = now.toFormat("dd MMMM yyyy");
  const classroom = user?.myClassroom;
  const plannedTotalWeeks = calculateTotalWeeks(
    classroom?.startDate,
    classroom?.endDate,
    classroom?.pace
  );

  const focusedWeekData = weeklyContent.find(
    (w) => w.weekNumber === focusedWeek
  );
  const currentWeekData = weeklyContent.find(
    (w) => w.weekNumber === currentWeek
  );

  const completedWeeksCount = weeklyContent.filter(
    (w) => w.status === ProgramWeekStatus.Completed
  ).length;

  // ✅ Use calculated progress (from module statuses) instead of backend value
  const displayOverallProgress =
    calculatedOverallProgress > 0 ? calculatedOverallProgress : overallProgress;

  // ✅ Calculate focused week progress locally
  const focusedWeekProgress = focusedWeekData
    ? calculateWeekProgress(focusedWeekData.modules)
    : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Summary Section */}
      <SummarySection>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: "bold", mb: 1 }}
        >
          My Classroom — {currentProgram || ""}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          <span>
            {formatDateRange(classroom?.startDate, classroom?.endDate)}
          </span>
          {" • "}
          <span>Today: {currentDate}</span>
        </Typography>
      </SummarySection>

      {/* KPI Section */}
      <KPISection>
        <OverallProgressCard>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Overall Progress
          </Typography>
          <LinearProgress
            variant="determinate"
            value={displayOverallProgress}
            sx={{
              height: 12,
              borderRadius: 6,
              bgcolor: theme.palette.grey[200],
              mb: 2,
              "& .MuiLinearProgress-bar": {
                bgcolor: theme.palette.primary.main,
                borderRadius: 6,
              },
            }}
          />
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            {Math.round(displayOverallProgress)}% complete
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {completedWeeksCount} of {plannedTotalWeeks || totalWeeks} weeks
            completed
          </Typography>
        </OverallProgressCard>

        <CurrentWeekCard>
          <Typography variant="h6" sx={{ mb: 1 }}>
            You're on Week {currentWeek}
          </Typography>

          {/* Next week unlock countdown - only show if next week exists */}
          {(() => {
            const nextWeek = weeklyContent.find(
              (w) => w.weekNumber === currentWeek + 1
            );
            if (nextWeek?.unlockAt) {
              const unlockDate = DateTime.fromISO(nextWeek.unlockAt, {
                zone: DEFAULT_TIMEZONE,
              });
              const now = DateTime.now().setZone(DEFAULT_TIMEZONE);
              // Only show if next week is still locked (unlock date in future)
              if (unlockDate > now) {
                return (
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Week {currentWeek + 1} unlocks:</strong>{" "}
                    {getUnlockCountdown(nextWeek.unlockAt)}
                  </Typography>
                );
              }
            }
            return null;
          })()}

          <Button
            variant="contained"
            color="primary"
            onClick={handleContinueLearning}
            startIcon={<PlayArrowIcon />}
            sx={{ mb: 1 }}
          >
            Continue Learning
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {(() => {
              if (!currentWeekData?.modules)
                return "Start your learning journey";

              const quizAvailable = currentWeekData.modules.find(
                (m) => getModuleStatus(m) === "quiz-available"
              );
              if (quizAvailable) {
                const label =
                  (quizAvailable as any).name ||
                  (quizAvailable as any).type ||
                  "Module";
                return `Quiz ready: ${label}`;
              }

              const inProgress = currentWeekData.modules.find(
                (m) => getModuleStatus(m) === "in-progress"
              );
              if (inProgress) {
                const label =
                  (inProgress as any).name ||
                  (inProgress as any).type ||
                  "Module";
                return `Continue: ${label}`;
              }

              const notStarted = currentWeekData.modules.find(
                (m) => getModuleStatus(m) === "not-started"
              );
              if (notStarted) {
                const label =
                  (notStarted as any).name ||
                  (notStarted as any).type ||
                  "Module";
                return `Next up: ${label}`;
              }

              return "Review completed modules";
            })()}
          </Typography>
        </CurrentWeekCard>
      </KPISection>

      {/* Main Content Tabs */}
      <Paper sx={{ mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{ px: 3, pt: 2 }}
          >
            <Tab
              label="Weekly Content"
              icon={<SchoolIcon />}
              iconPosition="start"
              sx={{ minHeight: 60 }}
            />
            <Tab
              label="Study Content"
              icon={<QuizIcon />}
              iconPosition="start"
              sx={{ minHeight: 60 }}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {currentTab === 0 && (
          <Box>
            {/* Enhanced Week Timeline Navigation */}
            <WeekTimeline>
              <TimelineHeader>
                <Typography variant="h6" sx={{ fontWeight: "medium" }}>
                  Weeks Timeline
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Week {currentWeek} of {plannedTotalWeeks || totalWeeks}
                </Typography>
              </TimelineHeader>
              <TimelineList>
                {allWeeksForTimeline.map((week) => {
                  const isClickable =
                    unlockAllWeeks || week.weekNumber <= currentWeek;
                  const isLocked = !isClickable;
                  const displayStatus = isLocked
                    ? ProgramWeekStatus.Locked
                    : week.status;

                  return (
                    <TimelineStep
                      key={week.weekId || week.weekNumber}
                      ref={week.weekNumber === currentWeek ? currentWeekRef : undefined}
                    >
                      <Tooltip
                        title={
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2" fontWeight="medium">
                              Week {week.weekNumber}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ display: "block" }}
                            >
                              {isLocked
                                ? "Locked"
                                : week.status === ProgramWeekStatus.Completed
                                ? "Completed ✓"
                                : week.status === ProgramWeekStatus.InProgress
                                ? `In Progress (${Math.round(week.progress)}%)`
                                : "Not Started"}
                            </Typography>
                            {isLocked && week.unlockAt && (
                              <>
                                <Typography
                                  variant="caption"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  {getUnlockCountdown(week.unlockAt)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ display: "block", opacity: 0.8 }}
                                >
                                  {week.unlockLabel}
                                </Typography>
                              </>
                            )}
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <span>
                          <TimelineButton
                            weekStatus={displayStatus}
                            onClick={() => handleWeekSelect(week.weekNumber)}
                            disabled={!unlockAllWeeks && isLocked}
                            aria-label={`Week ${week.weekNumber} ${
                              isLocked ? "(Locked)" : ""
                            }`}
                          >
                            {isLocked ? (
                              <LockIcon sx={{ fontSize: 18 }} />
                            ) : week.status === ProgramWeekStatus.Completed ? (
                              <CheckCircleIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <Typography variant="body1" fontWeight="bold">
                                {week.weekNumber}
                              </Typography>
                            )}
                          </TimelineButton>
                        </span>
                      </Tooltip>
                      <Typography
                        variant="caption"
                        color={isLocked ? "text.disabled" : "text.secondary"}
                        sx={{
                          textAlign: "center",
                          minHeight: 20,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {week.weekNumber === currentWeek
                          ? "Current"
                          : week.status === ProgramWeekStatus.Completed
                          ? "Done"
                          : isLocked && week.unlockLabel
                          ? week.unlockLabel
                          : ""}
                      </Typography>
                    </TimelineStep>
                  );
                })}
              </TimelineList>
            </WeekTimeline>

            {/* Focused Week Detail */}
            {focusedWeekData ? (
              <WeekDetailCard>
                <CardHeader
                  title={
                    <Typography variant="h5" component="h2">
                      {focusedWeekData.weekName}
                    </Typography>
                  }
                  subheader={
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ mt: 1 }}
                    >
                      <Chip
                        label={focusedWeekData.status.replace("_", " ")}
                        color={statusChipColorMap[focusedWeekData.status]}
                        size="small"
                      />
                      {focusedWeekData.startLabel && (
                        <Typography variant="body2" color="text.secondary">
                          Starts {focusedWeekData.startLabel}
                          {focusedWeekData.endLabel &&
                            ` • Ends ${focusedWeekData.endLabel}`}
                        </Typography>
                      )}
                    </Stack>
                  }
                  /* ✅ REMOVED: View Week Plan button - it did nothing */
                />
                <CardContent>
                  {/* Week Progress - ✅ FIXED: Use locally calculated progress */}
                  <Box sx={{ mb: 3 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="body1" fontWeight="medium">
                        Week Progress
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {
                          focusedWeekData.modules.filter(
                            (m) => getModuleStatus(m) === "completed"
                          ).length
                        }{" "}
                        of {focusedWeekData.modules.length} modules complete (
                        {focusedWeekProgress}%)
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={focusedWeekProgress}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        bgcolor: "grey.200",
                        "& .MuiLinearProgress-bar": {
                          bgcolor:
                            focusedWeekProgress === 100
                              ? "success.main"
                              : focusedWeekProgress > 0
                              ? "primary.main"
                              : "warning.main",
                          borderRadius: 6,
                        },
                      }}
                    />
                  </Box>

                  <Divider sx={{ mb: 3 }}>
                    <Chip label="Modules" variant="outlined" />
                  </Divider>

                  {/* Modules Grid */}
                  {focusedWeekData.modules &&
                  focusedWeekData.modules.length > 0 ? (
                    <ModuleTileGrid container spacing={3}>
                      {focusedWeekData.modules.map((module, index) => {
                        const moduleStatus = getModuleStatus(module);
                        const chipProps = getStatusChipProps(
                          moduleStatus,
                          module
                        );
                        const buttonProps = getModuleButtonProps(moduleStatus);
                        const secondaryInfo = getModuleSecondaryInfo(
                          moduleStatus,
                          module
                        );
                        const label =
                          (module as any).name ||
                          (module as any).type ||
                          "Module";
                        const iconKey =
                          (module as any).type || (module as any).name || "";

                        return (
                          <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={module.moduleId || index}
                          >
                            <ModuleTile
                              moduleStatus={moduleStatus}
                              onClick={() =>
                                handleModuleClick(
                                  module,
                                  focusedWeekData.weekNumber
                                )
                              }
                            >
                              <CardContent
                                sx={{
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <ModuleHeader>
                                  <ModuleIcon moduleStatus={moduleStatus}>
                                    {moduleIcons[iconKey] || <MenuBookIcon />}
                                  </ModuleIcon>
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      component="h3"
                                      sx={{ fontWeight: "medium" }}
                                    >
                                      {label}
                                    </Typography>
                                    <Chip
                                      label={chipProps.label}
                                      size="small"
                                      color={chipProps.color}
                                      variant={chipProps.variant}
                                    />
                                  </Box>
                                </ModuleHeader>

                                <Box sx={{ mb: 2, flexGrow: 1 }}>
                                  <Typography
                                    variant="body2"
                                    color={
                                      moduleStatus === "completed"
                                        ? "success.main"
                                        : moduleStatus === "quiz-available"
                                        ? "info.main"
                                        : "text.secondary"
                                    }
                                    fontWeight={
                                      moduleStatus === "completed" ||
                                      moduleStatus === "quiz-available"
                                        ? "medium"
                                        : "normal"
                                    }
                                  >
                                    {secondaryInfo}
                                  </Typography>

                                  {/* Progress bar for in-progress modules */}
                                  {moduleStatus === "in-progress" && (
                                    <LinearProgress
                                      variant="determinate"
                                      value={getModuleProgressPercent(module)}
                                      sx={{ mt: 1, height: 4, borderRadius: 2 }}
                                    />
                                  )}
                                </Box>

                                <ModuleActions>
                                  <Button
                                    variant={buttonProps.variant}
                                    color={buttonProps.color}
                                    size="small"
                                    startIcon={buttonProps.icon}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const shouldStartQuiz =
                                        moduleStatus === "quiz-available";
                                      handleModuleClick(
                                        module,
                                        focusedWeekData.weekNumber,
                                        shouldStartQuiz
                                      );
                                    }}
                                  >
                                    {buttonProps.label}
                                  </Button>
                                  {moduleStatus === "completed" && (
                                    <Button
                                      variant="text"
                                      size="small"
                                      color="primary"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const label =
                                          (module as any).name ||
                                          (module as any).type ||
                                          "Module";
                                        setSelectedModuleForAttempts({
                                          moduleId: module.moduleId,
                                          moduleName: `Week ${focusedWeekData.weekNumber} - ${label}`,
                                        });
                                        setAttemptsModalOpen(true);
                                      }}
                                    >
                                      View Attempts
                                    </Button>
                                  )}
                                </ModuleActions>
                              </CardContent>
                            </ModuleTile>
                          </Grid>
                        );
                      })}
                    </ModuleTileGrid>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No modules available for this week yet.
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </WeekDetailCard>
            ) : (
              <WeekDetailCard>
                <CardContent>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <LockIcon
                      sx={{ fontSize: 48, color: "text.disabled", mb: 2 }}
                    />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Week {focusedWeek} Content Not Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This week's content will be available once you progress
                      through earlier weeks.
                    </Typography>
                  </Box>
                </CardContent>
              </WeekDetailCard>
            )}

            {/* Helpful Guidance */}
            <GuidanceBox>
              <Typography
                variant="h6"
                sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
              >
                <CheckCircleIcon color="info" />
                Tips & Guidance
              </Typography>
              <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                {currentWeekData && (
                  <li>
                    <Typography variant="body2">
                      Complete all modules in Week {currentWeek} to unlock the
                      next week.
                    </Typography>
                  </li>
                )}
                <li>
                  <Typography variant="body2">
                    New content arrives every 7 days at 9:00am AEST.
                  </Typography>
                </li>
                {focusedWeekData &&
                  focusedWeekData.modules.some(
                    (m) => getModuleStatus(m) === "in-progress"
                  ) && (
                    <li>
                      <Typography variant="body2">
                        Finish incomplete modules to track your progress
                        accurately.
                      </Typography>
                    </li>
                  )}
                {focusedWeekData &&
                  focusedWeekData.modules.some(
                    (m) => getModuleStatus(m) === "quiz-available"
                  ) && (
                    <li>
                      <Typography variant="body2">
                        You have quizzes ready to take! Complete them to finish
                        your modules.
                      </Typography>
                    </li>
                  )}
              </Box>
            </GuidanceBox>
          </Box>
        )}

        {/* Study Content Tab */}
        {currentTab === 1 && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <SchoolIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>
              Practice Sessions Now Available in Modules
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Practice quizzes, flashcards, and exercises are now integrated
              directly into each module. Complete all sections in a module to
              unlock practice sessions before taking the final quiz.
            </Typography>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() => setCurrentTab(0)}
              sx={{
                backgroundColor: "#4A90A4",
                "&:hover": { backgroundColor: "#3A7B8E" },
              }}
            >
              Go to Modules
            </Button>
          </Box>
        )}
      </Paper>

      {/* Footer */}
      <Box sx={{ mt: 6, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          Times shown in AEST (Australia/Sydney).
        </Typography>
      </Box>
      {/* Quiz Attempts Modal */}
      <QuizAttemptsModal
        open={attemptsModalOpen}
        onClose={() => {
          setAttemptsModalOpen(false);
          setSelectedModuleForAttempts(null);
        }}
        moduleId={selectedModuleForAttempts?.moduleId || null}
        moduleName={selectedModuleForAttempts?.moduleName || ""}
      />
    </Container>
  );
};

export default MyClassroomPage;
